import React from 'react';
import moment from 'moment';
import { getMessage } from '@tcc/i18n';
import { inferUnit } from '../../i18n/unitQtyWrapper';
import styles from './css/HourlyForecast.css';
import { WeatherConditionsType } from '../types';
import { createUnitOrNotApplicable } from '../utils/createUnitOrNotApplicable';
import { FeatureFlagKey } from '../../types';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

const images = require.context('../images', true);
const newImages = require.context('../images/new', true);

export function HourlyForecast({
  hourlyConditions: {
    firstForecastTime,
    precipitationProbability,
    windSpeed,
    windDirection,
    weatherCondition,
  },
}) {
  const isSprayAssistEnabled = useFeatureFlag(FeatureFlagKey.DMB_SPRAY_ASSIST);
  const hours = [...Array(24)].map((hour, index) => {
    const hourMoment = moment(firstForecastTime).add(index, 'h');
    return index === 0 || hourMoment.format('h') === '12'
      ? hourMoment.format('ha')
      : hourMoment.format('h');
  });

  const windUnits = inferUnit('wind-speed').symbol.universal;

  // TODO: make decision on indicating night/day now that sunrise
  // and sunset are not returned from api call
  // TODO: determine whether hourly forecast begins with firstForecastTime (time returned from api)
  // or whether it begins with the hour before that (time used in weather app currently)

  return (
    <div
      className={styles.container}
      style={isSprayAssistEnabled ? { borderBottom: 0, paddingBottom: 0 } : {}}
    >
      <h2 className={styles.title}>{getMessage('forecast.hourly.title')}</h2>
      <div>
        <table>
          <tbody>
            <tr
              className={styles.hoursRow}
              data-test-id="hourly-forecast-hours"
            >
              <th>{getMessage('forecast.hourly.hour')}</th>
              {hours.map((hour) => (
                <td key={hour}>{hour}</td>
              ))}
            </tr>
            <tr className={styles.precipRow}>
              <th>{getMessage('forecast.hourly.precipPercent')}</th>
              {precipitationProbability.q.map((value) => (
                <td key={value}>
                  <div className={styles.precipBarContainer}>
                    <div>{`${value}%`}</div>
                    <div
                      className={styles.precipBar}
                      style={{ height: value / 2 }}
                    />
                  </div>
                </td>
              ))}
            </tr>
            <tr className={styles.windRow}>
              <th>{`${getMessage('forecast.hourly.wind')} (${windUnits})`}</th>
              {windSpeed.q.map((speed, index) => {
                const formattedWindSpeed = createUnitOrNotApplicable(
                  speed,
                  windSpeed.u
                ).forLocaleGroup('wind-speed');
                return (
                  <td key={speed}>
                    <div className={styles.windContainer}>
                      <div
                        style={{
                          transform: `rotate(${
                            180 + windDirection.q[index]
                          }deg)`,
                        }}
                        className={styles.windDirectionIconContainer}
                      >
                        {isSprayAssistEnabled ? (
                          <img
                            alt={getMessage('image.compass.needle')}
                            className={styles.windDirectionIcon}
                            src={images('./new/forecast/wind_direction.png')}
                          />
                        ) : (
                          `↑`
                        )}
                      </div>
                      <div>{formattedWindSpeed.scalarLocaleString}</div>
                    </div>
                  </td>
                );
              })}
            </tr>
            <tr className={styles.conditionsRow}>
              <th>{getMessage('forecast.hourly.conditions')}</th>
              {weatherCondition.data.map((condition) => (
                <td key={condition}>
                  <img
                    alt={condition}
                    src={
                      isSprayAssistEnabled
                        ? newImages(
                            `./forecast-icons/small/${condition.replace(
                              '_',
                              ''
                            )}.png`
                          )
                        : images(
                            `./forecast-icons/small/${condition.replace(
                              '_',
                              ''
                            )}.png`
                          )
                    }
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

HourlyForecast.propTypes = {
  hourlyConditions: WeatherConditionsType,
};
